import { useState, useEffect, useCallback } from 'react';
import { STORAGE_KEYS } from '../constants/storage';

export interface UserData {
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  isEmailVerified?: boolean;
}

export interface Role {
  id?: string;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export function useUser() {
  const [user, setUser] = useState<UserData | null>(() => {
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);
    try {
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  });

  const [roles, setRoles] = useState<Role[]>(() => {
    const rolesStr = localStorage.getItem(STORAGE_KEYS.ROLES);
    try {
      const parsed = rolesStr ? JSON.parse(rolesStr) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const [permissions, setPermissions] = useState<string[]>(() => {
    const permsStr = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
    try {
      const parsed = permsStr ? JSON.parse(permsStr) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const isAdmin = roles.some((role) => role.name === 'Admin');

  const refreshUser = useCallback(() => {
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);
    try {
      setUser(userStr ? JSON.parse(userStr) : null);
    } catch {
      setUser(null);
    }

    const rolesStr = localStorage.getItem(STORAGE_KEYS.ROLES);
    try {
      const parsed = rolesStr ? JSON.parse(rolesStr) : [];
      setRoles(Array.isArray(parsed) ? parsed : []);
    } catch {
      setRoles([]);
    }

    const permsStr = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
    try {
      const parsed = permsStr ? JSON.parse(permsStr) : [];
      setPermissions(Array.isArray(parsed) ? parsed : []);
    } catch {
      setPermissions([]);
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.USER || e.key === STORAGE_KEYS.ROLES || e.key === STORAGE_KEYS.PERMISSIONS) {
        refreshUser();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [refreshUser]);

  return { user, roles, permissions, isAdmin, refreshUser };
}
